import type { CSSProperties } from "react";

/*
 * Profile themes override Tailwind's own token variables.
 *
 * Tailwind v4 compiles `text-forest` to `var(--color-forest)`, so a theme that
 * only sets `--forest` (the legacy alias in styles.css) changes nothing on the
 * rendered profile — which is why picking a theme used to repaint the cover and
 * nothing else. Every name below has to keep the `--color-` prefix to reach the
 * utilities the profile is actually built from.
 */
export type ProfileThemePreset = {
  id: string;
  label: string;
  hint: string;
  /** Cover band and the dark score card. */
  cover: string;
  /** Primary accent: icons, borders, highlight rail. */
  accent: string;
  accentLight: string;
  /** Accent legible on dark surfaces. */
  soft: string;
  /** Very light accent wash for chips and highlighted rows. */
  tint: string;
  /** Accent at low opacity, for focus rings and shadows. */
  ring: string;
};

export const PROFILE_THEMES: ProfileThemePreset[] = [
  {
    id: "forest",
    label: "Grafite",
    hint: "O visual padrão do Score Creator.",
    cover: "#18131c",
    accent: "#660099",
    accentLight: "#7b1fa2",
    soft: "#d8b7f2",
    tint: "#f1e7f7",
    ring: "rgba(102,0,153,.16)",
  },
  {
    id: "sunset",
    label: "Ameixa",
    hint: "Roxo profundo e discreto.",
    cover: "#2b1b31",
    accent: "#5a1a73",
    accentLight: "#7a2d96",
    soft: "#c9acd9",
    tint: "#efe6f3",
    ring: "rgba(90,26,115,.16)",
  },
  {
    id: "violet",
    label: "Vivo",
    hint: "Violeta aberto e luminoso.",
    cover: "#24132d",
    accent: "#7b1fa2",
    accentLight: "#9a4bc0",
    soft: "#e1c8f0",
    tint: "#f4e9fa",
    ring: "rgba(123,31,162,.16)",
  },
  {
    id: "ocean",
    label: "Oceano",
    hint: "Azul sóbrio, bom para perfis corporativos.",
    cover: "#101c2b",
    accent: "#12557f",
    accentLight: "#1a74ab",
    soft: "#a9d8f0",
    tint: "#e4f0f8",
    ring: "rgba(18,85,127,.16)",
  },
  {
    id: "ember",
    label: "Brasa",
    hint: "Âmbar quente, alto contraste.",
    cover: "#2a1710",
    accent: "#a4471a",
    accentLight: "#c76a2c",
    soft: "#f2c39b",
    tint: "#fbeade",
    ring: "rgba(164,71,26,.16)",
  },
  {
    id: "jade",
    label: "Jade",
    hint: "Verde calmo e natural.",
    cover: "#10231d",
    accent: "#1c6b4f",
    accentLight: "#2a8d69",
    soft: "#a8dcc6",
    tint: "#e2f3ec",
    ring: "rgba(28,107,79,.16)",
  },
];

export const DEFAULT_PROFILE_THEME = PROFILE_THEMES[0]!;

export const PROFILE_THEME_IDS = PROFILE_THEMES.map((theme) => theme.id);

export function profileThemeById(preset?: string | null) {
  return PROFILE_THEMES.find((theme) => theme.id === preset) || DEFAULT_PROFILE_THEME;
}

export function profileThemeVars(preset?: string | null): CSSProperties {
  const theme = profileThemeById(preset);
  return {
    "--color-forest": theme.accent,
    "--color-forest-light": theme.accentLight,
    "--color-lime": theme.soft,
    "--forest": theme.accent,
    "--lime": theme.soft,
    "--profile-cover": theme.cover,
    "--profile-accent": theme.accent,
    "--profile-soft": theme.soft,
    "--profile-tint": theme.tint,
    "--profile-ring": theme.ring,
  } as CSSProperties;
}

/** Swatch used by the theme picker, so it always matches what renders. */
export function profileThemeSwatch(theme: ProfileThemePreset): CSSProperties {
  return { backgroundImage: `linear-gradient(135deg, ${theme.cover} 0 62%, ${theme.soft} 62%)` };
}
